import { useId, useMemo, useState, type ReactNode } from "react";
import type { RoleState } from "../../state/identity.tsx";
import { WriteButton, ReadOnlyNote, canWrite } from "./WriteControls.tsx";
import { PublicJwkField, Dialog } from "./write-forms.tsx";
import { useCreateSource, useRegisterSourceKey } from "./use-sources.ts";
import {
  AUDIT_KEY_POLICY_OPTIONS,
  SERVICE_OPTIONS,
  auditKeyPolicyLabel,
  validateKid,
  validatePublicJwk,
  validateSourceInput,
  validateThumbprint,
  writeErrorMessage,
  type AuditKeyPolicy,
  type Source,
  type SourceFormValues,
  type SourceKeyInput,
  type SourceValidationIssue,
} from "./vocab.ts";
import type { Service } from "@oathera/contract";

/**
 * The admin enrol wizard (task 11.14; design 13 "Source management", 12.4 "a labelled,
 * keyboard-operable multi-step form with clear step structure"). It walks the four manual steps:
 *
 *   1. CREATE the source — tenant (never in the body; from identity), service, name, environment,
 *      optional admin_url / jwks_url, and the audit-key policy (`pinned` or `tofu`).
 *   2. REGISTER the component's PUBLIC telemetry JWK (`purpose: telemetry`). The telemetry key PAIR
 *      is generated IN THE COMPONENT'S ENVIRONMENT; the control plane registers only the public JWK
 *      and NEVER sees a private key (FR-11.1). This step offers no "generate" button and no
 *      private-key field — only the public-JWK paste, validated to refuse any private member.
 *   3. PIN the audit signing key by `kid` + thumbprint, OR — for a `tofu` demo source — skip pinning
 *      (trust on first use is explicit; FR-11.5).
 *   4. SHOW the copy-able component config the admin takes to the component:
 *      `OATHERA_TELEMETRY_URL`, `OATHERA_TELEMETRY_SOURCE_ID`, and a REFERENCE to the private key
 *      (never the key itself — the control plane never holds it).
 *
 * The whole flow is one `<form>` per step inside a dialog; the step heading is announced and the
 * step list shows progress. Every write is admin-gated and surfaces the server's 400/403/422.
 */

type WizardStep = 1 | 2 | 3 | 4;

const STEP_TITLES: Readonly<Record<WizardStep, string>> = {
  1: "Create the source",
  2: "Register the public telemetry key",
  3: "Pin the audit signing key",
  4: "Configure the component",
};

export function EnrolWizard(props: { role: RoleState; onClose: () => void }): ReactNode {
  const titleId = useId();
  const [step, setStep] = useState<WizardStep>(1);
  const [created, setCreated] = useState<Source | null>(null);

  return (
    <Dialog titleId={titleId} title="Enroll a source" wide onClose={props.onClose}>
      <ol className="src-steps" aria-label="Enrollment steps">
        {([1, 2, 3, 4] as const).map((n) => (
          <li
            key={n}
            className="src-steps__item"
            data-state={n === step ? "current" : n < step ? "done" : "upcoming"}
            aria-current={n === step ? "step" : undefined}
          >
            <span className="src-steps__n" aria-hidden="true">
              {n < step ? "✓" : n}
            </span>
            {STEP_TITLES[n]}
          </li>
        ))}
      </ol>

      <ReadOnlyNote role={props.role} />

      {step === 1 && (
        <CreateStep
          role={props.role}
          onCreated={(source) => {
            setCreated(source);
            setStep(2);
          }}
          onCancel={props.onClose}
        />
      )}
      {step === 2 && created !== null && (
        <TelemetryKeyStep
          role={props.role}
          source={created}
          onDone={() => setStep(created.audit_key_policy === "tofu" ? 4 : 3)}
          onBack={() => setStep(1)}
        />
      )}
      {step === 3 && created !== null && (
        <AuditPinStep role={props.role} source={created} onDone={() => setStep(4)} onBack={() => setStep(2)} />
      )}
      {step === 4 && created !== null && <ConfigStep source={created} onClose={props.onClose} />}
    </Dialog>
  );
}

/* ---- Step 1: create the source -------------------------------------------------------------- */

function CreateStep(props: {
  role: RoleState;
  onCreated: (source: Source) => void;
  onCancel: () => void;
}): ReactNode {
  const mutation = useCreateSource();
  const [submitted, setSubmitted] = useState(false);
  const [values, setValues] = useState<SourceFormValues>({
    service: "",
    name: "",
    environment: "",
    adminUrl: "",
    jwksUrl: "",
    auditKeyPolicy: "pinned",
  });

  const validation = useMemo(() => validateSourceInput(values), [values]);
  const canSubmit = canWrite(props.role) && validation.ok && !mutation.isPending;

  const issueFor = (field: SourceValidationIssue["field"]): string | undefined =>
    validation.issues.find((i) => i.field === field)?.message;

  const set = <K extends keyof SourceFormValues>(key: K, value: SourceFormValues[K]): void =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const submit = (): void => {
    setSubmitted(true);
    if (!canSubmit || validation.input === undefined) return;
    mutation.mutate(validation.input, { onSuccess: (result) => props.onCreated(result.source) });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <p className="src-step__intro src-faint">
        The source is bound to your team automatically. The team is taken from your verified
        identity, never entered here.
      </p>

      <div className="src-field-row">
        <label className="src-field">
          <span className="src-field__label">Service</span>
          <select
            className="src-input"
            value={values.service}
            onChange={(e) => set("service", e.target.value as Service | "")}
            aria-invalid={submitted && issueFor("service") !== undefined}
          >
            <option value="">(choose a service)</option>
            {SERVICE_OPTIONS.map((svc) => (
              <option key={svc} value={svc}>
                {svc}
              </option>
            ))}
          </select>
          {submitted && issueFor("service") !== undefined && (
            <span className="src-field__error" role="alert">
              {issueFor("service")}
            </span>
          )}
        </label>

        <label className="src-field">
          <span className="src-field__label">Environment</span>
          <input
            className="src-input"
            value={values.environment}
            onChange={(e) => set("environment", e.target.value)}
            placeholder="prod, staging, …"
            maxLength={64}
            aria-invalid={submitted && issueFor("environment") !== undefined}
          />
          {submitted && issueFor("environment") !== undefined && (
            <span className="src-field__error" role="alert">
              {issueFor("environment")}
            </span>
          )}
        </label>
      </div>

      <label className="src-field">
        <span className="src-field__label">Name</span>
        <input
          className="src-input"
          value={values.name}
          onChange={(e) => set("name", e.target.value)}
          placeholder="e.g. gateway-mcp us-east"
          maxLength={128}
          aria-invalid={submitted && issueFor("name") !== undefined}
        />
        {submitted && issueFor("name") !== undefined && (
          <span className="src-field__error" role="alert">
            {issueFor("name")}
          </span>
        )}
      </label>

      <label className="src-field">
        <span className="src-field__label">Admin URL (optional, https)</span>
        <input
          className="src-input"
          type="url"
          value={values.adminUrl}
          onChange={(e) => set("adminUrl", e.target.value)}
          placeholder="https://…"
          maxLength={2048}
          aria-invalid={submitted && issueFor("admin_url") !== undefined}
        />
        {submitted && issueFor("admin_url") !== undefined && (
          <span className="src-field__error" role="alert">
            {issueFor("admin_url")}
          </span>
        )}
      </label>

      <label className="src-field">
        <span className="src-field__label">JWKS URL (optional, https)</span>
        <input
          className="src-input"
          type="url"
          value={values.jwksUrl}
          onChange={(e) => set("jwksUrl", e.target.value)}
          placeholder="https://…/.well-known/jwks.json"
          maxLength={2048}
          aria-invalid={submitted && issueFor("jwks_url") !== undefined}
        />
        {submitted && issueFor("jwks_url") !== undefined && (
          <span className="src-field__error" role="alert">
            {issueFor("jwks_url")}
          </span>
        )}
      </label>

      <fieldset className="src-fieldset">
        <legend>Audit-key policy</legend>
        {AUDIT_KEY_POLICY_OPTIONS.map((policy) => (
          <label key={policy} className="src-field src-field--radio">
            <input
              type="radio"
              name="audit-key-policy"
              value={policy}
              checked={values.auditKeyPolicy === policy}
              onChange={() => set("auditKeyPolicy", policy as AuditKeyPolicy)}
            />
            <span className="src-field__label">{auditKeyPolicyLabel(policy)}</span>
          </label>
        ))}
        <span className="src-field__hint src-faint">
          <strong>Pinned keys</strong> verify audit batches against keys you approve. <strong>Trust on
          first use</strong> is for demo sources only and is always shown explicitly.
        </span>
      </fieldset>

      {mutation.isError && (
        <p className="src-field__error" role="alert">
          {writeErrorMessage(mutation.error, "source creation")}
        </p>
      )}

      <div className="src-dialog__actions">
        <WriteButton role={props.role} type="submit" variant="primary" disabled={!canSubmit} onClick={submit}>
          {mutation.isPending ? "Creating…" : "Create source"}
        </WriteButton>
        <button type="button" className="src-btn" data-variant="ghost" onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/* ---- Step 2: register the public telemetry key ---------------------------------------------- */

function TelemetryKeyStep(props: {
  role: RoleState;
  source: Source;
  onDone: () => void;
  onBack: () => void;
}): ReactNode {
  const mutation = useRegisterSourceKey();
  const [kid, setKid] = useState("");
  const [jwkText, setJwkText] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const jwkValidation = useMemo(() => validatePublicJwk(jwkText), [jwkText]);
  const kidError = validateKid(kid);
  const canSubmit = canWrite(props.role) && jwkValidation.ok && kidError === null && !mutation.isPending;

  const submit = (): void => {
    setSubmitted(true);
    if (!canSubmit || jwkValidation.jwk === undefined) return;
    const body: SourceKeyInput = { purpose: "telemetry", kid: kid.trim(), public_jwk: jwkValidation.jwk };
    mutation.mutate({ id: props.source.id, body }, { onSuccess: () => props.onDone() });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <p className="src-step__intro src-faint">
        Generate the telemetry key pair <strong>in {props.source.name}’s environment</strong>, then paste
        its <strong>public</strong> JWK here. The control plane registers only the public key. It never
        generates, transmits or stores a private key.
      </p>

      <label className="src-field">
        <span className="src-field__label">Key id (`kid`)</span>
        <input
          className="src-input"
          value={kid}
          onChange={(e) => setKid(e.target.value)}
          placeholder="e.g. 2025-06-key"
          aria-invalid={submitted && kidError !== null}
        />
        {submitted && kidError !== null && (
          <span className="src-field__error" role="alert">
            {kidError}
          </span>
        )}
      </label>

      <PublicJwkField value={jwkText} onChange={setJwkText} validation={jwkValidation} showError={submitted} />

      {mutation.isError && (
        <p className="src-field__error" role="alert">
          {writeErrorMessage(mutation.error, "key registration")}
        </p>
      )}

      <div className="src-dialog__actions">
        <button type="button" className="src-btn" data-variant="ghost" onClick={props.onBack}>
          Back
        </button>
        <WriteButton role={props.role} type="submit" variant="primary" disabled={!canSubmit} onClick={submit}>
          {mutation.isPending ? "Registering…" : "Register public key"}
        </WriteButton>
      </div>
    </form>
  );
}

/* ---- Step 3: pin the audit signing key ------------------------------------------------------ */

function AuditPinStep(props: {
  role: RoleState;
  source: Source;
  onDone: () => void;
  onBack: () => void;
}): ReactNode {
  const mutation = useRegisterSourceKey();
  const [kid, setKid] = useState("");
  const [thumbprint, setThumbprint] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const kidError = validateKid(kid);
  const thumbError = validateThumbprint(thumbprint);
  const canSubmit = canWrite(props.role) && kidError === null && thumbError === null && !mutation.isPending;

  const submit = (): void => {
    setSubmitted(true);
    if (!canSubmit) return;
    const body: SourceKeyInput = { purpose: "audit", kid: kid.trim(), jwk_thumbprint: thumbprint.trim() };
    mutation.mutate({ id: props.source.id, body }, { onSuccess: () => props.onDone() });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <p className="src-step__intro src-faint">
        Pin the audit signing key the component will use, by its key id and RFC 7638 JWK thumbprint.
        Verification trusts pinned keys only. You can also skip this now and pin the key later from the
        source’s pins table once it has been observed.
      </p>

      <label className="src-field">
        <span className="src-field__label">Audit key id (`kid`)</span>
        <input
          className="src-input"
          value={kid}
          onChange={(e) => setKid(e.target.value)}
          placeholder="e.g. audit-2025-06"
          aria-invalid={submitted && kidError !== null}
        />
        {submitted && kidError !== null && (
          <span className="src-field__error" role="alert">
            {kidError}
          </span>
        )}
      </label>

      <label className="src-field">
        <span className="src-field__label">JWK thumbprint (base64url SHA-256, 43 characters)</span>
        <input
          className="src-input src-input--mono"
          value={thumbprint}
          onChange={(e) => setThumbprint(e.target.value)}
          placeholder="43-character base64url thumbprint"
          aria-invalid={submitted && thumbError !== null}
          spellCheck={false}
          autoComplete="off"
        />
        {submitted && thumbError !== null && (
          <span className="src-field__error" role="alert">
            {thumbError}
          </span>
        )}
      </label>

      {mutation.isError && (
        <p className="src-field__error" role="alert">
          {writeErrorMessage(mutation.error, "audit-key pin")}
        </p>
      )}

      <div className="src-dialog__actions">
        <button type="button" className="src-btn" data-variant="ghost" onClick={props.onBack}>
          Back
        </button>
        <button type="button" className="src-btn" data-variant="ghost" onClick={props.onDone}>
          Skip for now
        </button>
        <WriteButton role={props.role} type="submit" variant="primary" disabled={!canSubmit} onClick={submit}>
          {mutation.isPending ? "Pinning…" : "Pin audit key"}
        </WriteButton>
      </div>
    </form>
  );
}

/* ---- Step 4: component config (copy-able; never the private key) ---------------------------- */

function ConfigStep(props: { source: Source; onClose: () => void }): ReactNode {
  // The telemetry URL the component posts to. The control plane does not expose it in the contract,
  // so this is an honest template the admin completes with their ingest host — flagged as such.
  const telemetryUrl = "https://<your-oathera-ingest-host>/v1/ingest";

  const lines: readonly { readonly key: string; readonly value: string }[] = [
    { key: "OATHERA_TELEMETRY_URL", value: telemetryUrl },
    { key: "OATHERA_TELEMETRY_SOURCE_ID", value: props.source.id },
    { key: "OATHERA_TELEMETRY_PRIVATE_KEY_REF", value: "<reference to the private key you generated in this environment>" },
  ];
  const block = lines.map((l) => `${l.key}=${l.value}`).join("\n");

  return (
    <div className="src-config">
      <p className="src-step__intro">
        <strong>{props.source.name}</strong> is enrolled. Configure the component with the values below and
        take them to its environment. The private key stays in the component. The control plane never
        holds it, so the config references it, it does not contain it.
      </p>

      <CopyBlock label="Component environment variables" text={block} />

      <ul className="src-config__notes src-faint">
        <li>
          <code>OATHERA_TELEMETRY_URL</code> is a template. Fill in your ingest host. The contract does
          not publish the ingest URL, so it cannot be auto-filled here (flagged as an honest unknown).
        </li>
        <li>
          <code>OATHERA_TELEMETRY_SOURCE_ID</code> is this source’s id, matched on every batch.
        </li>
        <li>
          <code>OATHERA_TELEMETRY_PRIVATE_KEY_REF</code> points the component at the private key it holds.
          The private key never leaves the component and is never sent here.
        </li>
      </ul>

      <div className="src-dialog__actions">
        <button type="button" className="src-btn" data-variant="primary" onClick={props.onClose}>
          Done
        </button>
      </div>
    </div>
  );
}

/* ---- A copy-able code block ----------------------------------------------------------------- */

function CopyBlock(props: { label: string; text: string }): ReactNode {
  const [copied, setCopied] = useState(false);

  const copy = (): void => {
    const clip = globalThis.navigator?.clipboard;
    if (clip !== undefined) {
      void clip.writeText(props.text).then(
        () => {
          setCopied(true);
          globalThis.setTimeout(() => setCopied(false), 2000);
        },
        () => setCopied(false),
      );
    }
  };

  return (
    <div className="src-copy">
      <div className="src-copy__head">
        <span className="src-field__label">{props.label}</span>
        <button type="button" className="src-btn" data-variant="ghost" onClick={copy}>
          {copied ? "Copied ✓" : "Copy"}
        </button>
      </div>
      <pre className="src-copy__pre" aria-label={props.label}>
        <code>{props.text}</code>
      </pre>
    </div>
  );
}
